import { motion } from 'framer-motion'

import { CopyWithText, ImageComponent } from '@apps-common/components/common'
import { VertSpacerMd, VertSpacerXS } from '@apps-common/components/layout'
import { ModalButton } from '@apps-common/components/modals'
import { MESSAGE_VALUE_NONE } from '@apps-common/config/app-messages'
import { commonIcons } from '@apps-common/config/icons'
import { GRID_CARD_VARIANTS } from '@apps-common/config/motion-variants'
import { UNIT_STATUS_ACTIVE } from '@apps-common/config/unit-filter'
import { valueStringCheck } from '@apps-common/helpers/value-string-check'
import { useNavigation } from '@apps-common/hooks/useNavigation'
import { usePortalFirebaseUser } from '@apps-common/hooks/usePortalFirebaseUser'
import { modalSlideUpEnterAnimation, modalSlideUpLeaveAnimation } from '@apps-common/lib/modal-animations'
import { IMyMonitoringAccountUnitItemModel } from '@apps-common/types/portal/my-monitoring'
import { ROUTE_MY_MONITORING_ACCOUNT_DETAILS, ROUTE_UNIT_MANAGE } from '@apps-radionet-portal-root/src/config/private-routes'

import { ButtonRoundedWithIcon } from '@apps-common/components/elements'
import { MyMonitoringUnitModifyModal } from './MyMonitoringUnitModifyModal'

interface IProps {
  userId: number
  disabled?: boolean
  unit: IMyMonitoringAccountUnitItemModel
}

export const MyMonitoringAccountUnitCard: React.FC<IProps> = (props) => {
  const { userId, disabled, unit } = props
  const { unitId, accountNo, monitoringCentre, bureau, productImageUrl, siteName, userNumber, mapPartition, userPartition } = unit

  const { navigateForward } = useNavigation()
  const fbUser = usePortalFirebaseUser()

  return (
    <motion.div variants={GRID_CARD_VARIANTS} className='w-full rounded-[34px] p-4 overflow-hidden bg-surface border border-border'>
      <div className='flex items-stretch justify-between gap-[10px] h-full'>
        <div className='grow'>
          <div className='flex items-center justify-start'>
            <h4 className='jost-light text-[0.85rem] text-ellipsis whitespace-nowrap overflow-hidden flex items-center justify-start gap-[5px] text-text'>
              {fbUser?.isMonitoringCentre ? bureau : monitoringCentre}
            </h4>
          </div>
          <div className='flex items-center justify-between gap-[10px]'>
            <div className='flex items-center justify-start gap-[5px]'>
              <div className='px-4 py-2 h-[20px] rounded-[15px] flex items-center justify-start gap-[10px] min-w-0 bg-surface-2 border border-border'>
                <commonIcons.processor className='text-text' />
                <h5 className='jost-light text-[0.85rem] text-text uppercase'>{valueStringCheck(accountNo)}</h5>
              </div>
            </div>
            <div className='w-[45px] h-[45px] rounded-[23px] flex items-center justify-center overflow-hidden bg-surface-2 border border-border'>
              <ImageComponent
                src={productImageUrl}
                alt={`Product ${accountNo}`}
                imageClasses='max-w-[30px]'
                errorIconClasses='text-[1.2rem]'
              />
            </div>
          </div>
          <VertSpacerXS />

          <div className='flex flex-col items-start justify-start gap-[5px]'>
            <CopyWithText textValue={siteName} noTextValue='<No site info>' value={siteName} />
            <CopyWithText textValue='User#' value={userNumber} noValue={MESSAGE_VALUE_NONE} showCopyValue className='text-[0.75rem]' />
            <CopyWithText
              textValue='Partition#'
              value={userPartition}
              noValue={MESSAGE_VALUE_NONE}
              showCopyValue
              className='text-[0.75rem]'
            />
          </div>

          <VertSpacerMd />
          <div className='flex items-center justify-between gap-[6px]'>
            <ModalButton
              component={MyMonitoringUnitModifyModal}
              disabled={disabled}
              componentProps={{ userId, unitId, isEditing: true }}
              enterAnimation={modalSlideUpEnterAnimation}
              leaveAnimation={modalSlideUpLeaveAnimation}
              backdropDismiss
            >
              <ButtonRoundedWithIcon
                type='div'
                text='Edit'
                icon={<commonIcons.edit />}
                className='action-neutral !min-w-[90px]'
                iconContainerClassName='bg-border text-text'
              />
            </ModalButton>

            <ButtonRoundedWithIcon
              type='button'
              text='View device'
              icon={<commonIcons.arrowAngled />}
              className='action-brand grow'
              iconContainerClassName='bg-brand text-on-brand'
              callback={(e) => {
                e?.stopPropagation()
                navigateForward({
                  routeUrl: ROUTE_UNIT_MANAGE.buildPath(UNIT_STATUS_ACTIVE.value, unitId),
                  route: ROUTE_MY_MONITORING_ACCOUNT_DETAILS
                })
              }}
            />
          </div>
        </div>
      </div>
    </motion.div>
  )
}
